import { useState } from "react";
import { Plus, Trash2, Search } from "lucide-react";
import { fmt } from "@/lib/api";
import { ItemNameInput, searchItems } from "./ItemNameInput";

const blank = () => ({ key: Math.random().toString(36).slice(2), item_id: "", name: "", spec: "", category: "",
                       hsn: "", qty: 1, unit: "Pack", rate: "", tax_percent: 0 });
export const newPOLine = blank;

export const calcLine = (l) => {
    const amt = (Number(l.qty) || 0) * (Number(l.rate) || 0);
    const tax = amt * (Number(l.tax_percent) || 0) / 100;
    return { amt, tax, total: amt + tax };
};

export const POLinesTable = ({ lines, setLines, items, vendorRates = {} }) => {
    const [q, setQ] = useState("");
    const set = (key, patch) => setLines(lines.map((l) => (l.key === key ? { ...l, ...patch } : l)));
    const hits = searchItems(items, q);
    const fromItem = (it) => ({ item_id: it.id, name: it.name, category: it.category || "", hsn: it.hsn || "",
                                unit: it.unit || "Pack", tax_percent: it.tax_percent || 0,
                                rate: vendorRates[it.name.trim().toLowerCase()] ?? "" });
    const addItem = (it) => {
        const empty = lines.find((l) => !l.name.trim());
        if (empty) set(empty.key, fromItem(it));
        else setLines([...lines, { ...blank(), ...fromItem(it) }]);
        setQ("");
    };
    return (
        <div data-testid="po-lines">
            <div className="relative">
                <Search size={14} className="absolute left-3 top-3 text-slate-400"/>
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Add from item master — search name or HSN…"
                       className="hdb-input !pl-9" data-testid="po-item-search"/>
                {hits.length > 0 && (
                    <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-xl max-h-72 overflow-y-auto">
                        {hits.map((it) => (
                            <button key={it.id} onClick={() => addItem(it)} data-testid={`po-item-hit-${it.id}`}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex justify-between">
                                <span className="font-semibold">{it.name}</span><span className="text-xs text-slate-400">{it.category} · {it.tax_percent}%</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
            <div className="overflow-x-auto mt-3">
                <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-wider text-slate-500 text-left">
                        <tr><th className="py-2 pr-2">Item</th><th className="pr-2">Spec / Brand</th><th className="pr-2 w-20 text-right">Qty</th>
                            <th className="pr-2 w-20">Unit</th><th className="pr-2 w-24 text-right">Rate ₹</th><th className="pr-2 w-16 text-right">GST %</th>
                            <th className="pr-2 w-24 text-right">GST ₹</th><th className="pr-2 w-28 text-right">Total ₹</th><th className="w-8"/></tr>
                    </thead>
                    <tbody>
                        {lines.map((l, i) => {
                            const c = calcLine(l);
                            return (
                                <tr key={l.key} className="border-t border-slate-100 align-top" data-testid={`po-line-${i}`}>
                                    <td className="py-1.5 pr-2 min-w-[200px]">
                                        <ItemNameInput value={l.name} items={items} idx={i} onType={(v) => set(l.key, { name: v, item_id: "" })} onPick={(it) => set(l.key, fromItem(it))}/>
                                        <input className="hdb-input mt-1 !py-1 text-xs" value={l.category} placeholder="Category" onChange={(e) => set(l.key, { category: e.target.value })} data-testid={`po-line-cat-${i}`}/>
                                    </td>
                                    <td className="py-1.5 pr-2 min-w-[140px]"><input className="hdb-input" value={l.spec} onChange={(e) => set(l.key, { spec: e.target.value })} placeholder="e.g. Brooke Bond" data-testid={`po-line-spec-${i}`}/></td>
                                    <td className="py-1.5 pr-2"><input type="number" min="0" className="hdb-input hdb-mono text-right" value={l.qty} onChange={(e) => set(l.key, { qty: e.target.value })} data-testid={`po-line-qty-${i}`}/></td>
                                    <td className="py-1.5 pr-2"><input className="hdb-input" value={l.unit} onChange={(e) => set(l.key, { unit: e.target.value })} data-testid={`po-line-unit-${i}`}/></td>
                                    <td className="py-1.5 pr-2"><input type="number" min="0" step="0.01" className="hdb-input hdb-mono text-right" value={l.rate} onChange={(e) => set(l.key, { rate: e.target.value })} data-testid={`po-line-rate-${i}`}/></td>
                                    <td className="py-1.5 pr-2"><input type="number" min="0" className="hdb-input hdb-mono text-right" value={l.tax_percent} onChange={(e) => set(l.key, { tax_percent: e.target.value })} data-testid={`po-line-gst-${i}`}/></td>
                                    <td className="py-1.5 pr-2 text-right hdb-mono pt-3 text-slate-600" data-testid={`po-line-gstamt-${i}`}>{fmt(c.tax)}</td>
                                    <td className="py-1.5 pr-2 text-right hdb-mono pt-3 font-bold" data-testid={`po-line-total-${i}`}>{fmt(c.total)}</td>
                                    <td className="py-1.5 pt-2.5"><button onClick={() => setLines(lines.filter((x) => x.key !== l.key))} className="text-slate-400 hover:text-red-600" data-testid={`po-line-remove-${i}`}><Trash2 size={14}/></button></td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <button onClick={() => setLines([...lines, blank()])} className="hdb-btn hdb-btn-ghost text-xs mt-2" data-testid="po-add-row"><Plus size={12}/> Add new item row</button>
        </div>
    );
};
